import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { IApplicationData, IApplicationRentalUnits } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from '../../../../../../../queries/applicationQueries';
import { useRentalUnitGuidsCache } from './queries/useRentalUnitGuidsCache';
import { isMobileScreen } from '@apply/ui';
import { ShowIf } from '../../../../../../components/ShowIf';
import { MobileUnitInformation } from './MobileUnitInformation/MobileUnitInformation';
import { UnitInformationTable } from './UnitInformationTable/UnitInformationTable';
import { v4 as uuidv4 } from 'uuid';

interface UnitInformationProps {
    propertyGuid: string;
    type: string;
    unitInformationRows: number;
    includeOccupied: boolean;
    isReview?: boolean;
}

export const UnitInformation: React.FC<UnitInformationProps> = ({
    propertyGuid,
    type,
    unitInformationRows,
    includeOccupied,
    isReview = false,
}) => {
    const mobileScreen = isMobileScreen();
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const safeRentalUnitGuids = useRentalUnitGuidsCache(propertyGuid);

    const set_rentalUnitGuids = (newGuids: string[]) => {
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid ?? ''), (old) => {
            if (!old) return old;
            const items = old.information?.properties?.items ?? [];
            const propIdx = items.findIndex((p) => p.guid === propertyGuid);
            if (propIdx === -1) return old;
            const property = items[propIdx];
            const existingUnits = property?.rentalUnits ?? [];
            const newUnits = newGuids.map((guid) => {
                const existing = existingUnits.find((u) => u.guid === guid);
                return (
                    existing ?? ({
                        guid,
                        occupied: undefined,
                        rent: undefined,
                        squareFeet: undefined,
                        bedrooms: undefined,
                        bathrooms: undefined,
                        garageBays: undefined,
                    } as IApplicationRentalUnits)
                );
            });
            const newItems = [...items];
            newItems[propIdx] = { ...property, rentalUnits: newUnits };
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        items: newItems,
                    },
                },
            };
        });
    };

    useEffect(() => {
        if (!isReview && unitInformationRows) {
            if (unitInformationRows > 0 && safeRentalUnitGuids.length == 0) {
                //Setup initial state rental units
                const array: string[] = Array.from({ length: unitInformationRows }, () => uuidv4());
                set_rentalUnitGuids(array);
                return;
            } else if (unitInformationRows > safeRentalUnitGuids.length) {
                //We're adding rental units so add a delta amount of items to the rentalUnitGuids interface
                const delta =
                    (unitInformationRows ? unitInformationRows : 0) - (safeRentalUnitGuids?.length ? safeRentalUnitGuids?.length : 0);
                const newItems: string[] = Array.from({ length: delta }, () => uuidv4());
                const combined: string[] = [...safeRentalUnitGuids, ...newItems];
                set_rentalUnitGuids(combined);
                return;
            } else if (unitInformationRows < safeRentalUnitGuids.length) {
                //We're going to remove the delta from the end of the list
                set_rentalUnitGuids(safeRentalUnitGuids.slice(0, unitInformationRows));
                return;
            } else {
                //Same amount exists so do nothing
                return;
            }
        }
    }, [propertyGuid, unitInformationRows]);

    return (
        <>
            <ShowIf showIf={mobileScreen}>
                <MobileUnitInformation
                    propertyGuid={propertyGuid}
                    type={type}
                    rentalUnitGuids={safeRentalUnitGuids}
                    includeOccupied={includeOccupied}
                    isReview={isReview}
                />
            </ShowIf>
            <ShowIf showIf={!mobileScreen}>
                <UnitInformationTable
                    propertyGuid={propertyGuid}
                    type={type}
                    rentalUnitGuids={safeRentalUnitGuids}
                    includeOccupied={includeOccupied}
                    isReview={isReview}
                />
            </ShowIf>
        </>
    );
};
